import React from 'react';
import Layout from '../components/Layout';
import { Shield, Award, Users, Target, CheckCircle, TrendingUp } from 'lucide-react';

const About: React.FC = () => {
  const values = [
    {
      icon: Shield,
      title: 'Transparence Totale',
      description: 'Nous fournissons des informations complètes et honnêtes sur chaque bookmaker et offre que nous analysons.',
    },
    {
      icon: Award,
      title: 'Analyses Impartiales',
      description: 'Nos évaluations sont basées sur des critères objectifs et l\'expérience réelle des utilisateurs.',
    },
    {
      icon: Users,
      title: 'Focus sur le Parieur',
      description: 'Nous privilégions toujours ce qui est le mieux pour les parieurs français.',
    },
    {
      icon: Target,
      title: 'Mise à Jour Constante',
      description: 'Nous maintenons toutes les informations à jour pour refléter les conditions actuelles.',
    },
  ];

  const team = [
    {
      role: 'Expert en Bookmakers',
      description: 'Plus de 10 ans d\'expérience dans l\'analyse de plateformes de paris sportifs en ligne.',
    },
    {
      role: 'Analyste de Bonus',
      description: 'Spécialisé dans la recherche des meilleures offres et conditions de bonus.',
    },
    {
      role: 'Expert en Sécurité',
      description: 'Garantit que seuls les bookmakers licenciés et sécurisés sont recommandés.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'À Propos - Primorixx',
    description: 'Découvrez Primorixx, votre source fiable d\'analyses de sites de paris sportifs en France.',
    url: 'https://primorixx.com/apropos',
    mainEntity: {
      '@type': 'Organization',
      name: 'Primorixx',
      description: 'Plateforme d\'analyses et de comparaisons de sites de paris sportifs en France',
      url: 'https://primorixx.com',
    },
  };

  return (
    <Layout
      title="À Propos"
      description="Découvrez Primorixx, votre source fiable d'analyses de sites de paris sportifs en France. Découvrez notre mission, nos valeurs et notre équipe d'experts."
      canonicalUrl="/apropos"
      jsonLd={jsonLd}
    >
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
                À Propos de Primorixx
              </h1>
              <p className="text-xl text-dark-muted mb-8">
                Nous sommes des experts dédiés à aider les parieurs français à
                trouver les meilleurs sites de paris sportifs et les offres les
                plus avantageuses du marché.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold mb-8 gradient-text text-center">
                Notre Mission
              </h2>
              <div className="bg-dark-card border border-dark-border rounded-xl p-8 mb-8">
                <p className="text-lg text-dark-muted mb-6 leading-relaxed">
                  Chez Primorixx, notre mission est simple: fournir des analyses
                  détaillées, impartiales et à jour sur les meilleurs sites de
                  paris sportifs disponibles pour les parieurs en France. Nous croyons que
                  chacun mérite de parier sur des plateformes sécurisées, fiables et avec
                  des offres généreuses.
                </p>
                <p className="text-lg text-dark-muted leading-relaxed">
                  Nous travaillons sans relâche pour tester chaque bookmaker, analyser
                  chaque bonus et vérifier chaque aspect important pour garantir
                  que vous ayez toutes les informations nécessaires pour prendre
                  des décisions éclairées.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Nos Valeurs
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
                  >
                    <div className="w-12 h-12 bg-dark-accent/20 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-dark-accent" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {value.title}
                    </h3>
                    <p className="text-dark-muted">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Notre Équipe
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {team.map((member, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 text-center"
                >
                  <div className="w-20 h-20 bg-dark-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-10 h-10 text-dark-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {member.role}
                  </h3>
                  <p className="text-dark-muted text-sm">
                    {member.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
                Pourquoi Choisir Primorixx?
              </h2>
              <div className="space-y-6">
                {[
                  'Analyses détaillées de chaque aspect du bookmaker',
                  'Comparaisons directes entre différentes offres',
                  'Informations mises à jour régulièrement',
                  'Focus exclusif sur le marché français',
                  'Transparence totale dans toutes les évaluations',
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 bg-dark-card border border-dark-border rounded-lg p-4"
                  >
                    <CheckCircle className="w-6 h-6 text-dark-accent flex-shrink-0 mt-1" />
                    <p className="text-dark-muted text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default About;

